import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listFrequencies, listSubjectsByLevel } from "../services/teacher";
import { listClassrooms } from "../services/classroom";
import { listEvaluations } from "../services/evaluation";
import { listGradebooks, createGradebook, setGrade, finalizeGradebook } from "../services/gradebook";
import { getUserInfo, canEditGradebookAnyStatus, isProfessor, isCambridgeSucursal } from "../services/auth";
import { GRADEBOOK_STATUS_BADGE, GRADEBOOK_STATUS_LABEL_KEY } from "../utils/gradebookStatus";
import WizardSteps from "./shared/WizardSteps";
import { useConfirm } from "../hook/useConfirm";
import { levelKey } from "../utils/classesLabels";
import { letterForPercent } from "../utils/cambridgeGrading";

const TOTAL_STEPS = 2;
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const todayIso = () => new Date().toISOString().split("T")[0];
// Same values as ListaAvaliacoesLayer/CadastrarAvaliacaoLayer's PERIOD_OPTIONS.
// An "Avaliação" (e.g. "1ª ACS") is reusable across trimestres — it's the
// *gradebook* (pauta) that's trimestre-scoped: sge_infantil_core's
// POST /api/gradebook only rejects a duplicate when both evaluationId AND
// period match (routes/gradebook.js), so the same evaluationId can carry a
// separate pauta per trimestre. So the picker below must list every
// evaluation regardless of trimestre, and `period` instead selects which of
// that evaluation's (possibly several) pautas we're viewing/creating.
const PERIOD_OPTIONS = [
  { value: "Primeiro Trimestre", label: "1º Trimestre" },
  { value: "Segundo Trimestre", label: "2º Trimestre" },
  { value: "Terceiro Trimestre", label: "3º Trimestre" },
];

// Ported from sge_infantil_web/src/pages/grade/create/index.js — same wizard
// philosophy (Steps: pick the gradebook coordinates -> edit/finalize grades ->
// success), rebuilt with this project's own WizardSteps component instead of
// antd's <Steps>, following the same convention as CriarTurmaLayer.
const LancamentoNotasLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState(1);
  const [done, setDone] = useState(false);

  // Deep-link entry point: ListaNotasLayer's "Editar" button navigates here
  // with the gradebook it already had in hand, so Admin/Director
  // Pedagógico/Super Admin land straight on its grades — no need to
  // re-derive it via the level/turma/disciplina/avaliação cascade below.
  const [pendingEditGradebook] = useState(() => location.state?.editGradebook || null);

  const [year, setYear] = useState(currentYear);
  const [frequencies, setFrequencies] = useState([]);
  const [levelId, setLevelId] = useState("");
  const [classrooms, setClassrooms] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [period, setPeriod] = useState("Primeiro Trimestre");
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationId, setEvaluationId] = useState("");
  const [searchStudent, setSearchStudent] = useState("");

  const [gradebook, setGradebook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [finalizing, setFinalizing] = useState(false);

  const stepLabels = [t("nota.wizard.step.data"), t("nota.wizard.step.grades")];
  const isCambridge = isCambridgeSucursal();
  const maxGrade = isCambridge ? 100 : 20;

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  useEffect(() => {
    setClassId("");
    setSubjectId("");
    setEvaluationId("");
    setGradebook(null);
    if (!levelId) {
      setClassrooms([]);
      setSubjects([]);
      return;
    }
    listClassrooms(year).then((resp) => setClassrooms((resp.data || []).filter((c) => c.educationLevel === levelId))).catch(() => setClassrooms([]));
    listSubjectsByLevel(levelId).then((resp) => setSubjects(resp.data || [])).catch(() => setSubjects([]));
  }, [levelId, year]);

  useEffect(() => {
    setEvaluationId("");
    setGradebook(null);
    if (!levelId || !subjectId) {
      setEvaluations([]);
      return;
    }
    // Unfiltered on purpose — every evaluation of the disciplina is offered
    // here regardless of year/period; the gradebook lookup/creation below is
    // what actually validates and tells the user whether a pauta already
    // exists for the selected turma+trimestre.
    listEvaluations(levelId, subjectId)
      .then((resp) => setEvaluations(resp.data || []))
      .catch(() => setEvaluations([]));
  }, [levelId, subjectId]);

  const fetchGradebook = async () => {
    if (!levelId || !classId || !evaluationId) return null;
    setLoading(true);
    // Gradebook records don't store a `year` field (only evaluationDate) —
    // passing year here would filter out every real record. `period` is
    // required though, since the same evaluationId can have one pauta per
    // trimestre.
    try {
      const resp = await listGradebooks({ levelId, classId, evaluationId, period });
      const gb = (resp.data || [])[0] || null;
      setGradebook(gb);
      return gb;
    } catch (err) {
      toast.error(t("nota.error.load"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGradebook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, classId, evaluationId, period]);

  // Deep-linked from Lista de Notas: drop straight into the grades step
  // with the gradebook we were handed — deliberately doesn't touch
  // levelId/classId/subjectId/evaluationId (nothing on this step needs
  // them; handleFinalize below reads the finalized record straight off its
  // own response instead of re-querying by those ids).
  useEffect(() => {
    if (!pendingEditGradebook) return;
    setGradebook(pendingEditGradebook);
    setCurrentStep(2);
  }, [pendingEditGradebook]);

  const sortedGrades = useMemo(() => {
    if (!gradebook) return [];
    const term = searchStudent.trim().toLowerCase();
    return [...(gradebook.grades || [])]
      .filter((g) => !term || (g.student?.name || "").toLowerCase().includes(term))
      .sort((a, b) => (a.student?.name || "").localeCompare(b.student?.name || "", "pt"));
  }, [gradebook, searchStudent]);

  const handleCreateGradebook = async () => {
    const level = frequencies.find((f) => f.id === levelId);
    const classroom = classrooms.find((c) => c.id === classId);
    const subject = subjects.find((s) => s.id === subjectId);
    const evaluation = evaluations.find((e) => e.id === evaluationId);
    const user = getUserInfo();
    setCreating(true);
    try {
      await createGradebook({
        level: { id: level.id, description: level.description },
        subject: { id: subject.id, name: subject.subjectName },
        evaluation: { id: evaluation.id, name: evaluation.evaluationName, weight: evaluation.weight, evaluationType: evaluation.evaluationType, category: evaluation.category },
        classroom: { id: classroom.id, name: classroom.className },
        teacher: { id: user?.teacher?.id || user?.id, name: user?.teacher?.name || user?.name },
        period,
        evaluationDate: todayIso(),
      });
      toast.success(t("nota.success.gradebookCreated"));
      await fetchGradebook();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.error || t("nota.error.create"));
      return false;
    } finally {
      setCreating(false);
    }
  };

  const handleGradeChange = (studentId, value) => {
    setGradebook((prev) => ({
      ...prev,
      grades: prev.grades.map((g) => (g.student.id === studentId ? { ...g, grade: value } : g)),
    }));
  };

  const handleGradeSave = async (studentId, value) => {
    if (value === "" || value === null || value === undefined) return;
    const num = Number(value);
    if (isNaN(num) || num < 0 || num > maxGrade) {
      toast.error(isCambridge ? t("nota.error.invalidGradePercent") : t("nota.error.invalidGrade"));
      return;
    }
    setSavingId(studentId);
    try {
      await setGrade(gradebook.id, studentId, num);
    } catch (err) {
      toast.error(t("nota.error.save"));
    } finally {
      setSavingId(null);
    }
  };

  const handleFinalize = async () => {
    // Só o Professor perde mesmo o acesso à pauta depois de finalizada
    // (Admin/Director Pedagógico/Super Admin continuam a poder editá-la),
    // por isso só a ele é que avisamos que a edição fica bloqueada.
    const confirmMessage = isProfessor() ? t("nota.confirm.finalizeTeacher") : t("nota.confirm.finalize");
    if (!(await confirm(confirmMessage, { variant: "warning" }))) return;
    setFinalizing(true);
    try {
      // crudService.update() only returns the fields that actually changed
      // (status/grades/teacherNotes/updatedAt), not the full record — merge
      // it into the gradebook we already have instead of replacing it, and
      // instead of re-querying by levelId/classId/evaluationId (unset when
      // arriving here via the "Editar" deep-link from Lista de Notas).
      const resp = await finalizeGradebook(gradebook.id);
      setGradebook((prev) => ({ ...prev, ...resp.data }));
      toast.success(t("nota.success.finalized"));
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || t("nota.error.finalize"));
    } finally {
      setFinalizing(false);
    }
  };

  const nextStep = async () => {
    if (!levelId || !classId || !subjectId || !evaluationId) {
      toast.error(t(levelKey("nota.error.requiredFields")));
      return;
    }
    if (gradebook) {
      setCurrentStep(2);
      return;
    }
    if (await handleCreateGradebook()) setCurrentStep(2);
  };

  const restart = () => {
    setLevelId("");
    setClassId("");
    setSubjectId("");
    setPeriod("Primeiro Trimestre");
    setEvaluationId("");
    setGradebook(null);
    setSearchStudent("");
    setCurrentStep(1);
    setDone(false);
  };

  // Admin, Director Pedagógico e Super Admin podem continuar a editar as
  // notas independentemente do estado da pauta — todos os outros perfis só
  // editam enquanto estiver em DRAFT.
  const isEditable = gradebook?.status === "DRAFT" || canEditGradebookAnyStatus();

  const gradedCount = sortedGrades.filter((g) => g.grade !== null && g.grade !== undefined && g.grade !== "").length;
  const gradedPercent = sortedGrades.length ? Math.round((gradedCount / sortedGrades.length) * 100) : 0;

  // Grades already persist as they're typed (handleGradeSave, onBlur), so —
  // same as sge_infantil_web/src/pages/grade/create/index.js#handleSaveDraft —
  // this is just a "save & go back" convenience, not an extra API call.
  const handleSave = () => {
    toast.success(t("nota.success.saved"));
    navigate("/academico/lista-notas");
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("nota.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("nota.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='p-16 radius-8 bg-neutral-50'>
                    <div className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-6 g-3'>
                      <div>
                        <label className='form-label text-sm mb-8'>{t("turma.field.academicYear")}</label>
                        <select className='form-select' value={year} onChange={(e) => setYear(Number(e.target.value))}>
                          {YEAR_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className='form-label text-sm mb-8'>{t(levelKey("turma.field.educationLevel"))}</label>
                        <select className='form-select' value={levelId} onChange={(e) => setLevelId(e.target.value)}>
                          <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                          {frequencies.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className='form-label text-sm mb-8'>{t("turma.col.className")}</label>
                        <select className='form-select' value={classId} onChange={(e) => setClassId(e.target.value)} disabled={!levelId}>
                          <option value=''>{t("pauta.field.class.placeholder")}</option>
                          {classrooms.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.className}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className='form-label text-sm mb-8'>{t("disciplina.field.subjectName")}</label>
                        <select className='form-select' value={subjectId} onChange={(e) => setSubjectId(e.target.value)} disabled={!levelId}>
                          <option value=''>{t("avaliacao.field.subject.placeholder")}</option>
                          {subjects.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.subjectName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className='form-label text-sm mb-8'>{t("avaliacao.field.period")}</label>
                        <select className='form-select' value={period} onChange={(e) => setPeriod(e.target.value)}>
                          {PERIOD_OPTIONS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className='form-label text-sm mb-8'>{t("nota.field.evaluation")}</label>
                        <select className='form-select' value={evaluationId} onChange={(e) => setEvaluationId(e.target.value)} disabled={!subjectId}>
                          <option value=''>{t("nota.field.evaluation.placeholder")}</option>
                          {evaluations.map((ev) => (
                            <option key={ev.id} value={ev.id}>
                              {ev.evaluationName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {!evaluationId ? (
                    <div className='alert alert-info d-flex align-items-center gap-2 mt-24 mb-0'>
                      <Icon icon='mdi:information-outline' className='text-lg flex-shrink-0' />
                      {t(levelKey("nota.hint.selectEvaluation"))}
                    </div>
                  ) : loading ? (
                    <div className='d-flex align-items-center justify-content-center gap-2 py-24 text-secondary-light'>
                      <Icon icon='svg-spinners:ring-resize' className='text-lg' />
                      {t("global.loading")}
                    </div>
                  ) : gradebook ? (
                    <div className='alert alert-info d-flex align-items-center gap-2 mt-24 mb-0'>
                      <Icon icon='mdi:information-outline' className='text-lg flex-shrink-0' />
                      {t("nota.hint.gradebookExists")}
                    </div>
                  ) : (
                    <div className='alert alert-warning d-flex align-items-center gap-2 mt-24 mb-0'>
                      <Icon icon='mdi:alert-circle-outline' className='text-lg flex-shrink-0' />
                      {t("nota.hint.noGradebook")}
                    </div>
                  )}

                  <div className='form-group d-flex flex-column flex-sm-row justify-content-sm-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep} disabled={creating}>
                      {creating ? (
                        t("global.loading")
                      ) : gradebook ? (
                        <>{t("global.next")} <i className='ri-arrow-right-line' /></>
                      ) : (
                        <><i className='ri-add-line' /> {t("nota.action.createGradebook")}</>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && gradebook && (
                <fieldset className='wizard-fieldset show'>
                  <div className='d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-3 mb-16'>
                    <div className='d-flex flex-wrap align-items-center gap-3'>
                      <span className={`badge ${GRADEBOOK_STATUS_BADGE[gradebook.status] || "bg-neutral-200 text-neutral-600"}`}>
                        {t(GRADEBOOK_STATUS_LABEL_KEY[gradebook.status]) || gradebook.status}
                      </span>
                      <div className='d-flex align-items-center gap-8'>
                        <div
                          className='progress bg-primary-50 rounded-pill h-8-px'
                          style={{ width: 96 }}
                          role='progressbar'
                          aria-valuenow={gradedCount}
                          aria-valuemin={0}
                          aria-valuemax={sortedGrades.length}
                        >
                          <div className='progress-bar bg-primary-600 rounded-pill' style={{ width: `${gradedPercent}%` }} />
                        </div>
                        <span className='text-secondary-light text-sm text-nowrap'>
                          {t("nota.hint.gradedCount", { graded: gradedCount, total: sortedGrades.length })}
                        </span>
                      </div>
                    </div>
                    <div className='icon-field w-100' style={{ maxWidth: 280 }}>
                      <span className='icon'>
                        <Icon icon='mdi:magnify' />
                      </span>
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        placeholder={t("nota.field.searchStudent.placeholder")}
                        value={searchStudent}
                        onChange={(e) => setSearchStudent(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className='table-responsive'>
                    <table className='table bordered-table mb-0'>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>#</th>
                          <th>{t("presenca.col.name")}</th>
                          <th style={{ width: isCambridge ? 200 : 160 }}>{isCambridge ? t("nota.col.gradePercent") : t("nota.col.grade")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedGrades.map((g, idx) => (
                          <tr key={g.student.id}>
                            <td>{idx + 1}</td>
                            <td>{g.student.name}</td>
                            <td>
                              <div className='d-flex align-items-center gap-2'>
                                <div className='icon-field flex-grow-1'>
                                  <span className='icon'>
                                    <Icon icon='solar:pen-2-outline' />
                                  </span>
                                  <input
                                    type='number'
                                    min='0'
                                    max={maxGrade}
                                    step='0.1'
                                    className='form-control form-control-sm'
                                    value={g.grade === null || g.grade === undefined ? "" : g.grade}
                                    disabled={!isEditable || savingId === g.student.id}
                                    onChange={(e) => handleGradeChange(g.student.id, e.target.value)}
                                    onBlur={(e) => handleGradeSave(g.student.id, e.target.value)}
                                  />
                                </div>
                                {isCambridge && letterForPercent(g.grade) && (
                                  <span className='badge bg-primary-50 text-primary-600'>{letterForPercent(g.grade)}</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className='form-group d-flex flex-column flex-sm-row justify-content-sm-end gap-8 mt-24'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={() => navigate("/academico/lista-notas")} disabled={finalizing}>
                      {t("global.cancel")}
                    </button>
                    {isEditable && (
                      <button type='button' className='btn btn-outline-primary-600 px-32' onClick={handleSave} disabled={finalizing}>
                        {t("nota.action.save")}
                      </button>
                    )}
                    {gradebook?.status === "DRAFT" && (
                      <button type='button' className='form-wizard-submit btn btn-success-600 px-32' onClick={handleFinalize} disabled={finalizing}>
                        {finalizing ? t("global.loading") : t("nota.action.finalize")}
                      </button>
                    )}
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("nota.success.finalized")}</h6>
                  </div>
                  <div className='form-group d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-sm-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("nota.action.createAnother")}
                    </button>
                    <button type='button' className='btn btn-primary-600 px-32' onClick={() => navigate("/academico/lista-notas")}>
                      <i className='ri-arrow-left-line' /> {t("nota.breadcrumb.lista")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
      {ConfirmModal}
    </div>
  );
};

export default LancamentoNotasLayer;
